import { gql } from "generated-graphql";

/**
 * Dedicated, slim query for the 8700-12 submission stepper shell. Kept separate
 * from the shared `GET_SUBMISSION_QUERY` (which the read-only view uses) so the
 * stepper only fetches what the shell/overview need: the single facility, the
 * submission type/method (to gate the stepper), and the ordered/filtered
 * `stepperSteps`.
 */
export const GET_SUBMISSION_STEPPER_QUERY = gql(`
  query GetSubmissionStepper($id: ID!) {
    submission(id: $id) {
      id
      title
      submissionType
      submissionMethod
      usesStepper
      status
      encampStatus
      dueDate
      notes
      reasonForSubmittal
      reviewRequired
      peerReviewRequired
      reviewMode
      staffAssignee {
        id
        email
        person {
          first
          last
        }
      }
      reviewers {
        id
        isStaff
        isSigner
        isRequired
        reviewOrder
        dueDate
        reviewerStatus
        signerNameOverride
        signerEmailOverride
        user {
          id
          email
          # Turn-taking (isActiveReviewer) phases by user type, matching the
          # backend's startReviewWorkflow routing.
          type
          person {
            first
            last
          }
        }
      }
      facilitySubmissions {
        id
        facility {
          id
          name
          state
        }
      }
      stepperSteps {
        step
        status
        assignee {
          id
          email
          person {
            first
            last
          }
        }
      }
    }
  }
`);

/**
 * Fetches the frozen RCRA 8700-12 snapshot content for a submission. Used by the
 * read-only stepper view once a submission is frozen; the content is the
 * point-in-time `Resolved870012` payload (under `resolved`), so the read-only
 * view never reads the live tables. Returns null when no snapshot exists.
 */
export const GET_RCRA_8700_12_SNAPSHOT_CONTENT_QUERY = gql(`
  query Rcra870012SnapshotContent($submissionId: ID!) {
    rcra870012SnapshotContent(submissionId: $submissionId)
  }
`);

/**
 * Submission comment thread for the stepper Overview. Kept separate from the
 * shell query so comments (and their optimistic add/delete) live with the
 * Overview that renders them, and are fetched at every lifecycle stage.
 */
export const GET_SUBMISSION_STEPPER_COMMENTS_QUERY = gql(`
  query GetSubmissionStepperComments($id: ID!) {
    submission(id: $id) {
      id
      comments {
        ...CommentThread
      }
    }
  }
`);
